import { useRef, useState } from 'react';
import SearchInput from '../../Atoms/Inputs/SearchInput';
import styles from './ContactMessages.module.scss';

/**
 * Filter bar for the contact-messages list.
 *
 * MULTI-COLUMN FILTERING (client request, 2026-09-23). «تصفية حسب / القيمة» is
 * a CRITERIA BUILDER: pick a field, give it a value, press «إضافة» (or Enter),
 * and the criterion becomes a removable chip. Repeat for as many columns as
 * needed; the table shows messages matching ALL of them. One criterion per
 * column — a field already in use is disabled in the select, because two
 * values for the same column under AND semantics can only fight each other.
 *
 * WHY CRITERIA COMMIT ON «إضافة» RATHER THAN FILTERING AS YOU TYPE. The API
 * answers HTTP 500 — not an empty list — for a value a field cannot parse
 * (see Hooks/ContactMessages.js), so firing half-typed values at it is asking
 * for errors an operator cannot interpret. An explicit add also gives the
 * builder one mental model: what is in the chips is what filters the table,
 * exactly, always.
 *
 * HOW MANY CRITERIA REACH THE SERVER. The DPA API accepts exactly ONE
 * `filterBy`/`filterValue` pair — a second pair is not ignored, it is
 * unsupported. The split is therefore: one chip → the pair goes to the server
 * precisely as before; two or more → the page fetches the full filtered-by-
 * dates-and-search set (`pageSize: 0`, a measured capability of this API) and
 * applies every chip locally with the same matching semantics the server was
 * measured to use. That decision lives in `ContactMessagesPage`, not here —
 * this component only builds and displays the criteria.
 *
 * DESIGN NOTES (unchanged from the single-filter version)
 *
 * - **The value control follows the field.** Picking "الحالة" swaps the text
 *   box for a two-option select; "الجهة المعنية" offers the known addresses
 *   from `/api/ContactUs/Settings`; "الرقم المرجعي" is a number input.
 * - **`FILTER_FIELDS` is a CLOSED LIST.** Every entry was verified against the
 *   live API on 2026-09-22; anything else — `createdOn` and `id` among them —
 *   is a 500. Do not add a field without checking it first.
 * - **The date inputs bound each other** so an impossible range cannot be
 *   expressed.
 * - **Clear is only shown when something is set.**
 */

/** Fields an operator would realistically filter by, with API field names. */
const FILTER_FIELDS = [
  { value: '', label: 'اختر الحقل' },
  { value: 'referenceNumber', label: 'الرقم المرجعي' },
  { value: 'fullName', label: 'اسم المرسل' },
  { value: 'email', label: 'بريد المرسل' },
  { value: 'mobile', label: 'رقم الجوال' },
  { value: 'subject', label: 'الموضوع' },
  { value: 'message', label: 'نص الرسالة' },
  { value: 'receiverEmail', label: 'الجهة المعنية' },
  { value: 'isRead', label: 'الحالة' },
];

const READ_STATE_OPTIONS = [
  { value: '', label: 'اختر الحالة' },
  { value: 'false', label: 'جديدة (غير مقروءة)' },
  { value: 'true', label: 'مقروءة' },
];

const fieldLabelOf = (field) =>
  FILTER_FIELDS.find((f) => f.value === field)?.label ?? field;

const ContactMessagesFilters = ({
  search,
  onSearch,
  fromDate,
  toDate,
  fieldFilters = [],
  onAddFilter,
  onRemoveFilter,
  onChange,
  onClear,
  recipients = [],
}) => {
  // The pending (not yet added) criterion is PRIVATE to the bar: nothing
  // filters until «إضافة», so the page never needs to know about half-built
  // input, and no debounce is needed anywhere in this flow.
  const [pendingField, setPendingField] = useState('');
  const [pendingValue, setPendingValue] = useState('');
  const fieldSelectRef = useRef(null);

  const activeCount =
    [search, fromDate, toDate].filter(Boolean).length + fieldFilters.length;
  const hasFilters = activeCount > 0;

  const canAdd = Boolean(pendingField && pendingValue.trim());

  const submitPending = () => {
    if (!canAdd) return;
    onAddFilter(pendingField, pendingValue.trim());
    setPendingField('');
    setPendingValue('');
    // Back to the field select, so chaining criteria is a keyboard-only flow:
    // choose field, type value, Enter, choose the next field.
    fieldSelectRef.current?.focus();
  };

  /** Enter in a value control adds the criterion instead of doing nothing. */
  const handleValueKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    submitPending();
  };

  /** Changing the field invalidates whatever value was typed for the old one. */
  const handleFieldChange = (value) => {
    setPendingField(value);
    setPendingValue('');
  };

  const handleClear = () => {
    setPendingField('');
    setPendingValue('');
    onClear();
  };

  /** Chip text: enumerated fields show their labels, not their wire values. */
  const displayValueOf = (criterion) => {
    if (criterion.field === 'isRead') {
      return (
        READ_STATE_OPTIONS.find((o) => o.value === criterion.value)?.label ??
        criterion.value
      );
    }
    if (criterion.field === 'receiverEmail') {
      return (
        recipients.find((r) => r.email === criterion.value)?.title ??
        criterion.value
      );
    }
    return criterion.value;
  };

  const renderValueControl = () => {
    if (!pendingField) {
      return (
        <input
          type='text'
          className={styles['filter-input']}
          value=''
          disabled
          placeholder='اختر الحقل أولاً'
          aria-label='قيمة الفلتر'
        />
      );
    }

    if (pendingField === 'isRead') {
      return (
        <select
          className={styles['filter-input']}
          value={pendingValue}
          onChange={(e) => setPendingValue(e.target.value)}
          aria-label='قيمة الفلتر'
        >
          {READ_STATE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }

    if (pendingField === 'receiverEmail') {
      return (
        <select
          className={styles['filter-input']}
          value={pendingValue}
          onChange={(e) => setPendingValue(e.target.value)}
          aria-label='قيمة الفلتر'
        >
          <option value=''>اختر الجهة</option>
          {/* Deactivated departments stay in the list, labelled. Messages sent
              while one was live are still in the table, and hiding its address
              here would make them unfindable by department. */}
          {recipients.map((r) => (
            <option key={r.email} value={r.email}>
              {r.isActive === false ? `${r.title} (غير مُفعّلة)` : r.title}
            </option>
          ))}
        </select>
      );
    }

    if (pendingField === 'referenceNumber') {
      // A reference number is an integer. A number input gives the right
      // keyboard on a tablet and rejects letters outright, rather than sending
      // "abc" to the server and getting a 500 back.
      return (
        <input
          type='number'
          min='1'
          step='1'
          className={`${styles['filter-input']} ${styles.ltr}`}
          value={pendingValue}
          onChange={(e) => setPendingValue(e.target.value)}
          onKeyDown={handleValueKeyDown}
          placeholder='مثال: 1'
          aria-label='قيمة الفلتر'
        />
      );
    }

    return (
      <input
        type='text'
        className={styles['filter-input']}
        value={pendingValue}
        onChange={(e) => setPendingValue(e.target.value)}
        onKeyDown={handleValueKeyDown}
        placeholder='أدخل القيمة'
        aria-label='قيمة الفلتر'
      />
    );
  };

  return (
    <section className={styles['filters']} aria-label='تصفية الرسائل'>
      <div className={styles['filters-row']}>
        <div className={styles['filter-group']}>
          <label className={styles['filter-label']} htmlFor='cm-search'>
            بحث سريع
          </label>
          <SearchInput search={search} setSearch={onSearch} />
        </div>

        <div className={styles['filter-group']}>
          <label className={styles['filter-label']} htmlFor='cm-from'>
            من تاريخ
          </label>
          <input
            id='cm-from'
            type='date'
            className={styles['filter-input']}
            value={fromDate}
            // Cannot be later than `to`, so the range is always valid.
            max={toDate || undefined}
            onChange={(e) => onChange({ fromDate: e.target.value })}
          />
        </div>

        <div className={styles['filter-group']}>
          <label className={styles['filter-label']} htmlFor='cm-to'>
            إلى تاريخ
          </label>
          <input
            id='cm-to'
            type='date'
            className={styles['filter-input']}
            value={toDate}
            min={fromDate || undefined}
            onChange={(e) => onChange({ toDate: e.target.value })}
          />
        </div>

        <div className={styles['filter-group']}>
          <label className={styles['filter-label']} htmlFor='cm-field'>
            تصفية حسب
          </label>
          <select
            id='cm-field'
            ref={fieldSelectRef}
            className={styles['filter-input']}
            value={pendingField}
            onChange={(e) => handleFieldChange(e.target.value)}
          >
            {FILTER_FIELDS.map((f) => (
              <option
                key={f.value}
                value={f.value}
                // One criterion per column: a field already in the chips can
                // only be re-filtered after removing its chip.
                disabled={
                  f.value !== '' &&
                  fieldFilters.some((c) => c.field === f.value)
                }
              >
                {f.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles['filter-group']}>
          <label className={styles['filter-label']}>القيمة</label>
          <div className={styles['filter-value-row']}>
            {renderValueControl()}
            <button
              type='button'
              className={styles['filter-add']}
              onClick={submitPending}
              disabled={!canAdd}
              aria-label='إضافة الفلتر'
            >
              إضافة
            </button>
          </div>
        </div>
      </div>

      {fieldFilters.length > 0 && (
        <ul className={styles['filter-chips']} aria-label='الفلاتر المفعّلة'>
          {fieldFilters.map((criterion) => (
            <li key={criterion.field} className={styles['filter-chip']}>
              <span className={styles['chip-field']}>
                {fieldLabelOf(criterion.field)}:
              </span>
              <span className={styles['chip-value']}>
                {displayValueOf(criterion)}
              </span>
              <button
                type='button'
                className={styles['chip-remove']}
                onClick={() => onRemoveFilter(criterion.field)}
                aria-label={`إزالة فلتر ${fieldLabelOf(criterion.field)}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {hasFilters && (
        <div className={styles['filters-foot']}>
          <span className={styles['filters-count']}>
            {activeCount} فلتر مُفعّل
            {fieldFilters.length > 1 &&
              ' — تُعرض الرسائل المطابقة لجميع الفلاتر معًا'}
          </span>
          <button
            type='button'
            className={styles['filters-clear']}
            onClick={handleClear}
          >
            مسح الكل
          </button>
        </div>
      )}
    </section>
  );
};

export default ContactMessagesFilters;
